'use client';

import { VoiceoverModelId } from 'ml-text-service-client';
import React, { useState, useRef, type JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, getAccessibleFocusStyles } from '@videoblocks/design-system';
import { useCloseOnOutsideClick } from '@videoblocks/design-system/hooks';
import ChevronDown from '@videoblocks/react-icons/ChevronDown';
import InformationCircle from '@videoblocks/react-icons/InformationCircle';

interface ModelOption {
    id: VoiceoverModelId;
    label: string;
    description: React.ReactNode;
}

const MODEL_OPTIONS: ModelOption[] = [
    {
        id: VoiceoverModelId.ElevenV3,
        label: 'Eleven v3',
        description: (
            <span className="inline-flex items-center gap-1">
                Expressive voice generation with audio tag support
                <a
                    href="https://help.storyblocks.com/en/articles/14667786-how-to-use-audio-tags-in-storyblocks-ai-voiceover-with-eleven-v3"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Learn more about audio tags"
                    className="hover:text-white"
                    onClick={(e) => e.stopPropagation()}
                >
                    <InformationCircle className="w-3.5 h-3.5 fill-current -translate-y-px" />
                </a>
            </span>
        ),
    },
    {
        id: VoiceoverModelId.ElevenMultilingualV2,
        label: 'Eleven Multilingual v2',
        description: (
            <>
                Lifelike voice generation with{' '}
                <a
                    href="https://help.storyblocks.com/en/articles/14667771-what-languages-do-you-support-with-ai-voiceover"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    multilingual support
                </a>
            </>
        ),
    },
];

export interface TextInputModelSelectProps {
    value: VoiceoverModelId;
    onChange: (modelId: VoiceoverModelId) => void;
    className?: string;
}

export const TextInputModelSelect = ({ value, onChange, className }: TextInputModelSelectProps): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = MODEL_OPTIONS.find((opt) => opt.id === value) ?? MODEL_OPTIONS[0];

    useCloseOnOutsideClick(dropdownOpen, setDropdownOpen, dropdownRef);

    return (
        <div className="relative w-full md:w-auto" ref={dropdownRef}>
            <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={twMerge(
                    'flex w-full md:w-auto justify-center items-center gap-1.5 rounded-capsule border border-gray-500 px-3 py-3 bg-[#252c2d] cursor-pointer min-w-[124px] hover:bg-gray-700',
                    getAccessibleFocusStyles(),
                    className,
                )}
            >
                <Text fontWeight="semibold">Model:</Text>
                <Text fontWeight="regular">{selectedOption.label}</Text>
                <ChevronDown
                    className={`fill-current w-3 h-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {dropdownOpen && (
                <div className="absolute top-full mt-1 overflow-clip rounded-md border border-gray-700 bg-gray-900 shadow-xl z-10 w-[335px]">
                    {MODEL_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => {
                                onChange(option.id);
                                setDropdownOpen(false);
                            }}
                            className={twMerge(
                                'w-full px-3 text-left cursor-pointer transition-colors',
                                option.id === value
                                    ? 'bg-[#36414d] pt-3 pb-2'
                                    : 'bg-[#1a1c1e] pt-2 pb-3 hover:bg-gray-800',
                            )}
                        >
                            <Text fontWeight="semibold" className="text-white">
                                {option.label}
                            </Text>
                            <Text fontSize="xs" fontWeight="regular" className="text-gray-400">
                                {option.description}
                            </Text>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
